import { create } from "zustand";

import {
  DEFAULT_PROJECT_GROUPING_MODE,
  type ProjectGroupingMode,
  type ProjectGroupingOverrides,
} from "./projectGrouping";

const PERSISTED_STATE_KEY = "forge:project-grouping:v1";

/**
 * How each repository's checkouts are grouped in the sidebar.
 *
 * Client-only and per-device on purpose. Unlike a project's name or its model
 * default — which are properties of the project and so are stored server-side
 * and fanned out to every checkout — this is a view preference about how one
 * person wants their own sidebar arranged. Writing it to every checkout would
 * rearrange other people's sidebars.
 *
 * Keyed by the repository's canonical key. See `resolveProjectGroupingMode` for
 * why it cannot be keyed by group.
 */
interface ProjectGroupingState {
  readonly overrides: ProjectGroupingOverrides;
  readonly setMode: (canonicalKey: string, mode: ProjectGroupingMode) => void;
}

function readPersisted(): ProjectGroupingOverrides {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(PERSISTED_STATE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return {};
    const result: Record<string, ProjectGroupingMode> = {};
    for (const [key, value] of Object.entries(parsed)) {
      // Anything else is a value this version does not understand. Dropping it
      // is safer than trusting it: an unknown string reaching the key derivation
      // would silently group by neither mode.
      if (value === "package" || value === "repository") result[key] = value;
    }
    return result;
  } catch {
    return {};
  }
}

function persist(overrides: ProjectGroupingOverrides): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(PERSISTED_STATE_KEY, JSON.stringify(overrides));
  } catch {
    // A full or blocked localStorage must not take the sidebar down with it;
    // the preference simply does not survive the reload.
  }
}

export const useProjectGroupingStore = create<ProjectGroupingState>((set, get) => ({
  overrides: readPersisted(),
  setMode: (canonicalKey, mode) => {
    // The default is stored as an ABSENT key rather than an explicit value, so
    // a project that has never been touched and one reset back to the default
    // are the same state. Otherwise the map would accumulate an entry per
    // repository anyone ever opened.
    const next = { ...get().overrides };
    if (mode === DEFAULT_PROJECT_GROUPING_MODE) {
      delete next[canonicalKey];
    } else {
      next[canonicalKey] = mode;
    }
    set({ overrides: next });
    persist(next);
  },
}));
